import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { clearStartupGrace } from '@/hooks/common/startupGrace'
import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  integrationsMutationKeys,
  integrationsQueryKeys,
} from '@/hooks/integrations/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { StatusResponse } from '@/types/Status'

type SyncableIntegrationProvider = 'salesforce'

interface SyncIntegrationArgs {
  orgId: UUID | ''
  provider: SyncableIntegrationProvider
  silent?: boolean
}

const syncIntegration = async (
  orgId: UUID | '',
  provider: SyncableIntegrationProvider,
  accessToken: string,
  silent?: boolean,
): Promise<void> => {
  if (!orgId) throw new Error('Organization ID is required')
  let url: string
  switch (provider) {
    case 'salesforce':
      url = `${API_URL}/api/v1/jobs/pull-salesforce/key/${orgId}`
      break
    default:
      const exhaustiveCheck: never = provider
      throw new Error(`Unknown provider: ${exhaustiveCheck}`)
  }
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

export const useSyncIntegration = ({
  orgId,
  provider,
  silent = false,
}: SyncIntegrationArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<void, Error, void, void>({
    mutationKey: integrationsMutationKeys.syncStart({ orgId, provider }),
    mutationFn: () => syncIntegration(orgId, provider, accessToken),
    onMutate: async () => {
      const key = integrationsQueryKeys.orgSyncStatus({ orgId, provider })
      await queryClient.cancelQueries({ queryKey: key })
      switch (provider) {
        case 'salesforce':
          queryClient.setQueryData<StatusResponse>(key, {
            status: 'pending',
            job_type: 'pull-salesforce',
          })
          break
      }
    },
    onSuccess: () => {
      const key = integrationsQueryKeys.orgSyncStatus({ orgId, provider })
      return queryClient.invalidateQueries({ queryKey: key })
    },
    onError: (error) => {
      handleError(error)
    },
  })
}
